import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import {
  collection,
  query,
  getDocs,
  orderBy,
  Timestamp,
  serverTimestamp,
  doc,
  setDoc
} from "firebase/firestore";
import { db } from "../../services/firebase";
import { UserAuth } from "../../services/AuthContext";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import BackspaceTwoToneIcon from "@mui/icons-material/BackspaceTwoTone";
import Typography from "@mui/material/Typography";

import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Close";

import { EditToolbar } from "../../common/edittoolbar";

import { DataGrid, GridActionsCellItem, GridRowModes } from "@mui/x-data-grid";

export const EditUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(true);
  const [rowModesModel, setRowModesModel] = useState({});
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 10,
    page: 0,
  });
  const navigate = useNavigate();
  const { user } = UserAuth();

  useEffect(() => {
    const fetchData = async () => {
      const q = query(collection(db, "users"), orderBy("email"));
      const qs = await getDocs(q);

      const docs = [];

      qs.forEach((doc) => {
        docs.push({
          ...doc.data(),
          id: doc.id,
        });
      });

      setLoading(true);
      setUsers(docs);
      setLoading(false);
    };

    fetchData();
  }, [active]);

  const columns = [
    { field: "email", headerName: "Email", flex: 1, editable: true },
    { field: "name", headerName: "Name", flex: 1, editable: true },
    { field: "driverNumber", headerName: "Driver #", flex: 1, editable: true },
    { field: "authProvider", headerName: "Provider", flex: 1, editable: false },
    {
      field: "role",
      headerName: "Role",
      flex: 1,
      editable: true,
      type: "singleSelect",
      valueOptions: ["Reporting", "Admin", "Unassigned"],
    },
    {
      field: "active",
      headerName: "Active",
      flex: 1,
      type: "boolean",
      editable: true,
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      width: 100,
      cellClassName: "actions",
      getActions: ({ id }) => {
        //console.log(rowModesModel);
        const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;

        if (isInEditMode) {
          return [
            <GridActionsCellItem
              icon={<SaveIcon />}
              label="Save"
              onClick={handleSaveClick(id)}
            />,
            <GridActionsCellItem
              icon={<CancelIcon />}
              label="Cancel"
              className="textPrimary"
              onClick={handleCancelClick(id)}
              color="inherit"
            />,
          ];
        }

        return [
          <GridActionsCellItem
            icon={<EditIcon />}
            label="Edit"
            className="textPrimary"
            onClick={handleEditClick(id)}
            color="inherit"
          />,
          <GridActionsCellItem
            icon={<DeleteIcon />}
            label="Delete"
            onClick={handleDeleteClick(id)}
            color="inherit"
          />,
        ];
      },
    },
  ];

  // https://codesandbox.io/s/0o2gny?file=/demo.js:2661-3076
  const handleRowEditStart = (params, event) => {
    event.defaultMuiPrevented = true;
  };

  const handleRowEditStop = (params, event) => {
    event.defaultMuiPrevented = true;
  };

  const handleEditClick = (id) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
  };

  const handleSaveClick = (id) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
  };

  const handleDeleteClick = (id) => async () => {
    //setRows(rows.filter((row) => row.id !== id));
    setLoading(true);
    //const done = await new Promise((res) => setTimeout(res, 2500));
    const deleted = {
      active: false,
      lastUpdatedBy: user.uid,
      lastUpdated: serverTimestamp()
    }

    const docRef = doc(db, 'users', id);
    await setDoc(docRef, deleted, {merge: true});
    setLoading(false);
    setUsers(users.map(u => {
      if (u.id === id){
        return {...u, active: false};
      }
      return u;
    }));
  };

  const handleCancelClick = (id) => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    });
  };

  const handleNew = async () => {
    const id = "randomId()";

    setLoading(true);
    const done = await new Promise((res) => setTimeout(res, 2500));
    setLoading(false);

    setUsers((oldRows) => [
      ...oldRows,
      {
        id,
        email: "",
        name: "",
        driverNumber: "",
        role: "",
        active: true,
        lastUpdated: new Timestamp(),
        isNew: true,
      },
    ]);
    setRowModesModel((oldModel) => ({
      ...oldModel,
      [id]: { mode: GridRowModes.Edit, fieldToFocus: "email" },
    }));
  };

  const processRowUpdate = async (newRow) => {

    const updatedRow = { ...newRow, isNew: false };
    //setRows(rows.map((row) => (row.id === newRow.id ? updatedRow : row)));

    setLoading(true);
    //const done = await new Promise((res) => setTimeout(res, 2500));

    newRow.lastUpdatedBy = user.uid;
    newRow.lastUpdated = serverTimestamp();

    const docRef = doc(db, 'users', newRow.id);
    await setDoc(docRef, newRow, {merge: true});

    setLoading(false);

    return updatedRow;
  };

  const handleRowModesModelChange = (newRowModesModel) => {
    setRowModesModel(newRowModesModel);
  };

  return (
    <Stack
      direction="column"
      justifyContent="center"
      alignItems="stretch"
      spacing={1}
    >
      <Box display="flex" justifyContent="space-between" p={2}>
        <Typography variant="h5">Manage Users</Typography>

        <Box display="flex">
          <Button
            variant="text"
            onClick={() => navigate('/maintenance')}
            startIcon={<BackspaceTwoToneIcon />}
          >
            Back
          </Button>
        </Box>
      </Box>
      <DataGrid
        loading={loading}
        autoHeight
        density="compact"
        pageSizeOptions={[5, 10, 25]}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        columns={columns}
        rows={users}
        editMode="row"
        rowModesModel={rowModesModel}
        onRowModesModelChange={handleRowModesModelChange}
        onRowEditStart={handleRowEditStart}
        onRowEditStop={handleRowEditStop}
        processRowUpdate={processRowUpdate}
        slotProps={{
          toolbar: { setActive, handleNew, label: "User" },
        }}
        initialState={{
          columns: { columnVisibilityModel: { authProvider: false } },
        }}
      />
    </Stack>
  );
};
